import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  setDoc,
} from "firebase/firestore";
import { db } from "../services/firebase";
import { logFirebaseAccessError } from "./firebasePermissionDebug";

let activeWorkspace = null;
let unsubscribeJobs = null;
let unsubscribeRecords = null;

const emitSyncStatus = (detail) => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("manucalc:cloud-sync-status", {
    detail: {
      workspacePath: activeWorkspace?.path?.join("/") || "",
      at: new Date().toISOString(),
      ...detail,
    },
  }));
};

const timestampValue = (value) => {
  const parsed = new Date(value || 0).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const stripLargeInlineData = (value, key = "") => {
  if (Array.isArray(value)) {
    return value.map((item) => stripLargeInlineData(item));
  }
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([, item]) => item !== undefined)
        .map(([childKey, item]) => [childKey, stripLargeInlineData(item, childKey)])
    );
  }
  if (
    typeof value === "string" &&
    (key === "dataUrl" || key === "fileDataUrl" || value.length > 150000)
  ) {
    return "";
  }
  return value;
};

const getWorkspacePath = (user, accountProfile) => {
  const companyId =
    accountProfile?.companyId ||
    accountProfile?.companyOwnerUid ||
    "";
  if (companyId) return ["companies", companyId];
  return ["users", user.uid];
};

const getWorkspaceCollection = (name) => {
  if (!activeWorkspace) return null;
  return collection(db, ...activeWorkspace.path, name);
};

const mergeByUpdatedAt = (localItems, cloudItems) => {
  const merged = new Map();
  [...localItems, ...cloudItems].forEach((item) => {
    if (!item?.id) return;
    const current = merged.get(item.id);
    if (!current || timestampValue(item.updatedAt) >= timestampValue(current.updatedAt)) {
      merged.set(item.id, item);
    }
  });
  return [...merged.values()].filter((item) => item.deleted !== true);
};

export const queueCloudJobSave = (job) => {
  const target = getWorkspaceCollection("manufacturingPlans");
  if (!target || !job?.id) {
    emitSyncStatus({ status: "local", scope: "manufacturingPlans", message: "Cloud workspace is not connected yet." });
    return;
  }
  emitSyncStatus({ status: "syncing", scope: "manufacturingPlans", recordId: job.id, message: "Saving manufacturing plan..." });
  setDoc(doc(target, job.id), stripLargeInlineData({ ...job, deleted: false }), { merge: true })
    .then(() => {
      emitSyncStatus({ status: "synced", scope: "manufacturingPlans", recordId: job.id, message: "Manufacturing plan synced." });
    })
    .catch((error) => {
    logFirebaseAccessError({
      error,
      path: `${activeWorkspace?.path?.join("/") || "workspace"}/manufacturingPlans/${job.id}`,
      operation: "create/update",
      user: { uid: activeWorkspace?.uid },
      context: "queueCloudJobSave",
    });
    emitSyncStatus({ status: "error", scope: "manufacturingPlans", recordId: job.id, message: "Manufacturing plan saved locally, but cloud sync failed." });
    console.warn("Manufacturing plan cloud sync failed:", error);
  });
};

export const queueCloudJobDelete = (jobId) => {
  const target = getWorkspaceCollection("manufacturingPlans");
  if (!target || !jobId) {
    emitSyncStatus({ status: "local", scope: "manufacturingPlans", message: "Cloud workspace is not connected yet." });
    return;
  }
  emitSyncStatus({ status: "syncing", scope: "manufacturingPlans", recordId: jobId, message: "Deleting manufacturing plan..." });
  setDoc(doc(target, jobId), {
    id: jobId,
    deleted: true,
    updatedAt: new Date().toISOString(),
  }, { merge: true })
    .then(() => {
      emitSyncStatus({ status: "synced", scope: "manufacturingPlans", recordId: jobId, message: "Manufacturing plan delete synced." });
    })
    .catch((error) => {
      logFirebaseAccessError({
        error,
        path: `${activeWorkspace?.path?.join("/") || "workspace"}/manufacturingPlans/${jobId}`,
        operation: "update",
        user: { uid: activeWorkspace?.uid },
        context: "queueCloudJobDelete",
      });
      emitSyncStatus({ status: "error", scope: "manufacturingPlans", recordId: jobId, message: "Manufacturing plan deleted locally, but cloud sync failed." });
      console.warn("Manufacturing plan cloud delete failed:", error);
    });
};

export const queueCloudRecordSave = (type, record) => {
  const target = getWorkspaceCollection("workspaceRecords");
  if (!target || !type || !record?.id) {
    emitSyncStatus({ status: "local", scope: "workspaceRecords", recordType: type, message: "Cloud workspace is not connected yet." });
    return;
  }
  const cloudId = `${type}--${record.id}`;
  emitSyncStatus({ status: "syncing", scope: "workspaceRecords", recordType: type, recordId: record.id, message: "Saving shop record..." });
  setDoc(
    doc(target, cloudId),
    stripLargeInlineData({ ...record, recordType: type, deleted: false }),
    { merge: true }
  )
    .then(() => {
      emitSyncStatus({ status: "synced", scope: "workspaceRecords", recordType: type, recordId: record.id, message: "Shop record synced." });
    })
    .catch((error) => {
    logFirebaseAccessError({
      error,
      path: `${activeWorkspace?.path?.join("/") || "workspace"}/workspaceRecords/${cloudId}`,
      operation: "create/update",
      user: { uid: activeWorkspace?.uid },
      context: "queueCloudRecordSave",
    });
    emitSyncStatus({ status: "error", scope: "workspaceRecords", recordType: type, recordId: record.id, message: "Record saved locally, but cloud sync failed." });
    console.warn("Shop record cloud sync failed:", error);
  });
};

export const queueCloudRecordDelete = (type, recordId) => {
  const target = getWorkspaceCollection("workspaceRecords");
  if (!target || !type || !recordId) {
    emitSyncStatus({ status: "local", scope: "workspaceRecords", recordType: type, message: "Cloud workspace is not connected yet." });
    return;
  }
  emitSyncStatus({ status: "syncing", scope: "workspaceRecords", recordType: type, recordId, message: "Deleting shop record..." });
  setDoc(doc(target, `${type}--${recordId}`), {
    id: recordId,
    recordType: type,
    deleted: true,
    updatedAt: new Date().toISOString(),
  }, { merge: true })
    .then(() => {
      emitSyncStatus({ status: "synced", scope: "workspaceRecords", recordType: type, recordId, message: "Shop record delete synced." });
    })
    .catch((error) => {
      logFirebaseAccessError({
        error,
        path: `${activeWorkspace?.path?.join("/") || "workspace"}/workspaceRecords/${type}--${recordId}`,
        operation: "update",
        user: { uid: activeWorkspace?.uid },
        context: "queueCloudRecordDelete",
      });
      emitSyncStatus({ status: "error", scope: "workspaceRecords", recordType: type, recordId, message: "Record deleted locally, but cloud sync failed." });
      console.warn("Shop record cloud delete failed:", error);
    });
};

export const stopWorkspaceCloudSync = () => {
  unsubscribeJobs?.();
  unsubscribeRecords?.();
  unsubscribeJobs = null;
  unsubscribeRecords = null;
  emitSyncStatus({ status: "local", message: "Cloud workspace sync stopped." });
  activeWorkspace = null;
};

export const startWorkspaceCloudSync = async ({
  user,
  accountProfile,
  getLocalJobs,
  mergeCloudJobs,
  getRecordTypes,
  getLocalRecords,
  mergeCloudRecords,
}) => {
  stopWorkspaceCloudSync();
  if (!user?.uid) return () => {};

  activeWorkspace = {
    path: getWorkspacePath(user, accountProfile),
    uid: user.uid,
  };
  emitSyncStatus({ status: "syncing", scope: "workspace", message: "Connecting workspace sync..." });

  const jobsCollection = getWorkspaceCollection("manufacturingPlans");
  const recordsCollection = getWorkspaceCollection("workspaceRecords");

  let jobsSnapshot;
  let recordsSnapshot;
  try {
    [jobsSnapshot, recordsSnapshot] = await Promise.all([
      getDocs(jobsCollection),
      getDocs(recordsCollection),
    ]);
  } catch (error) {
    logFirebaseAccessError({
      error,
      path: `${activeWorkspace.path.join("/")}/manufacturingPlans|workspaceRecords`,
      operation: "read",
      user,
      role: accountProfile?.role,
      companyId: accountProfile?.companyId || accountProfile?.companyOwnerUid,
      context: "startWorkspaceCloudSync initial load",
    });
    emitSyncStatus({ status: "error", scope: "workspace", message: "Cloud workspace could not load. Local data is still available." });
    throw error;
  }

  const cloudJobs = jobsSnapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
  const localJobs = getLocalJobs();
  const mergedJobs = mergeByUpdatedAt(localJobs, cloudJobs);
  mergeCloudJobs(mergedJobs);
  mergedJobs.forEach((job) => queueCloudJobSave(job));

  const cloudRecordsByType = {};
  recordsSnapshot.docs.forEach((item) => {
    const data = item.data();
    if (!data.recordType) return;
    if (!cloudRecordsByType[data.recordType]) cloudRecordsByType[data.recordType] = [];
    cloudRecordsByType[data.recordType].push({ id: data.id, ...data });
  });

  getRecordTypes().forEach((type) => {
    const merged = mergeByUpdatedAt(
      getLocalRecords(type),
      cloudRecordsByType[type] || []
    );
    mergeCloudRecords(type, merged);
    merged.forEach((record) => queueCloudRecordSave(type, record));
  });

  unsubscribeJobs = onSnapshot(jobsCollection, (snapshot) => {
    const cloud = snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
    mergeCloudJobs(mergeByUpdatedAt(getLocalJobs(), cloud));
    emitSyncStatus({ status: "synced", scope: "manufacturingPlans", message: "Manufacturing plans are synced." });
  }, (error) => {
    logFirebaseAccessError({
      error,
      path: `${activeWorkspace?.path?.join("/") || "workspace"}/manufacturingPlans`,
      operation: "listen",
      user,
      role: accountProfile?.role,
      companyId: accountProfile?.companyId || accountProfile?.companyOwnerUid,
      context: "manufacturingPlans snapshot",
    });
    emitSyncStatus({ status: "error", scope: "manufacturingPlans", message: "Manufacturing plan live sync stopped." });
  });

  unsubscribeRecords = onSnapshot(recordsCollection, (snapshot) => {
    const byType = {};
    snapshot.docs.forEach((item) => {
      const data = item.data();
      if (!data.recordType) return;
      if (!byType[data.recordType]) byType[data.recordType] = [];
      byType[data.recordType].push({ id: data.id, ...data });
    });
    Object.entries(byType).forEach(([type, cloud]) => {
      mergeCloudRecords(type, mergeByUpdatedAt(getLocalRecords(type), cloud));
    });
    emitSyncStatus({ status: "synced", scope: "workspaceRecords", message: "Shop records are synced." });
  }, (error) => {
    logFirebaseAccessError({
      error,
      path: `${activeWorkspace?.path?.join("/") || "workspace"}/workspaceRecords`,
      operation: "listen",
      user,
      role: accountProfile?.role,
      companyId: accountProfile?.companyId || accountProfile?.companyOwnerUid,
      context: "workspaceRecords snapshot",
    });
    emitSyncStatus({ status: "error", scope: "workspaceRecords", message: "Shop record live sync stopped." });
  });

  emitSyncStatus({ status: "synced", scope: "workspace", message: "Workspace sync connected." });

  return stopWorkspaceCloudSync;
};

export const mergeWorkspaceRecordsByUpdatedAt = mergeByUpdatedAt;
